<div class="lanes-grid-wrapper" #wrapperEl [style.maxHeight.px]="wrapperMaxHeight()">
  <app-lead-time-notice [leadTimeMinutes]="leadTimeMinutes()" />

  @if (loading()) {
    <div class="loading-indicator">
      <span class="loading-spinner"></span>
      <span>Lade Platzbelegung...</span>
    </div>
  }

  <!-- Day view (design 3a): nav arrows + centered day pill ABOVE the
       scrollbox — this row names the selected day (crucial in expanded
       layout, where the calendar is hidden) and hosts the prev/next
       navigation, which day view therefore drops from the time gutter.
       Deliberately NOT gated on hasData(): on an empty day (venue closed)
       the nav must stay reachable so the user can leave that day even in
       expanded layout. -->
  @if (!loading() && !isWeekView() && days()[0]; as day) {
    <div class="day-nav-header">
      <div class="day-nav-buttons">
        <button
          type="button"
          class="gutter-nav-btn"
          [title]="navPrevTitle()"
          [attr.aria-label]="navPrevTitle()"
          (click)="onNavPrev()">‹</button>
        <button
          type="button"
          class="gutter-nav-btn"
          [title]="navNextTitle()"
          [attr.aria-label]="navNextTitle()"
          (click)="onNavNext()">›</button>
      </div>
      <span class="day-pill">
        <span class="day-pill-name">{{ dayNameLabel(day) }}</span>
        <span class="day-pill-date">{{ dayDateLabel(day) }}</span>
        @if (day.isToday) {
          <span class="day-pill-badge">Heute</span>
        }
      </span>
    </div>
  }

  @if (!loading() && !hasData()) {
    <div class="no-data-message">
      Keine Plätze verfügbar für den gewählten Zeitraum.
    </div>
  }

  @if (!loading() && hasData()) {
    <div class="grid-scroll" #scrollContainer>
      <div class="grid-inner" [style.minWidth.px]="innerMinWidth()">
        <div class="grid-row" [class.week]="isWeekView()">
          <div class="time-gutter" [style.width.px]="gutterWidth()" [style.height.px]="totalHeightPx()">
            <div class="gutter-header" [style.height.px]="headerHeightPx()">
              <!-- Week only — day view's nav lives in the 3a day-nav-header
                   row above the scrollbox. -->
              @if (isWeekView()) {
                <button
                  type="button"
                  class="gutter-nav-btn"
                  [title]="navPrevTitle()"
                  [attr.aria-label]="navPrevTitle()"
                  (click)="onNavPrev()">‹</button>
                <button
                  type="button"
                  class="gutter-nav-btn"
                  [title]="navNextTitle()"
                  [attr.aria-label]="navNextTitle()"
                  (click)="onNavNext()">›</button>
              }
            </div>
            @for (tick of timeAxisTicks(); track tick.label + tick.top) {
              <div class="time-axis-label" [style.top.px]="tick.top">{{ tick.label }}</div>
            }
            <!-- Current-time chip — scrolls and sticks exactly like the hour
                 ticks. Label may be empty (tick-collision rule, see nowChip). -->
            @if (nowChip(); as chip) {
              <div class="now-time-chip" [style.top.px]="chip.top">{{ chip.label }}</div>
            }
          </div>

          @for (day of days(); track day.date) {
            <div
              class="day-group"
              [attr.data-day]="day.date"
              [style.minWidth.px]="dayGroupMinWidth()">
              <div class="day-header" [style.height.px]="headerHeightPx()">
                <!-- Week (design 1W): the day-view pill at week scale (.week
                     modifier) over small grey court labels. Day (design 3a):
                     court names only — the day is named by the day-nav-header
                     pill row above the scrollbox. -->
                @if (isWeekView()) {
                  <div class="day-pill week" [class.today]="day.isToday">
                    <span class="day-pill-name">{{ dayNameLabel(day) }}</span>
                    <span class="day-pill-date">{{ dayDateLabel(day) }}</span>
                  </div>
                }
                <div class="court-labels" [class.week]="isWeekView()">
                  @for (court of day.courts; track court.courtId) {
                    <div class="court-label" [title]="court.courtName">{{ court.courtName }}</div>
                  }
                </div>
              </div>
              <div class="lanes-row" [class.week]="isWeekView()">
                @for (court of day.courts; track court.courtId) {
                  <app-lanes-court-column
                    [column]="court"
                    [dateStr]="day.date"
                    [pxPerMinute]="pxPerMinute()"
                    [intervalMinutes]="intervalMinutes()"
                    [maxBookableUtc]="maxBookableUtc()"
                    [compact]="isWeekView()"
                    [selection]="selectionFor(day.date, court.courtId)"
                    (freeCellActivated)="onFreeCellActivated($event)"
                    (freeCellDragStarted)="onFreeCellDragStarted($event)"
                    (selectionBodyPointerDown)="onSelectionBodyPointerDown($event)"
                    (selectionResizeStarted)="onSelectionResizeStarted($event)"
                    (courseSessionClicked)="onCourseSessionClicked($event)" />
                }
              </div>
            </div>
          }
        </div>

        <!-- Current-time line — spans all lanes (and across the day gaps in
             week view), starting just after the sticky time column. -->
        @if (nowLineTop() !== null) {
          <div class="now-line" [style.left.px]="gutterWidth()" [style.top.px]="nowLineTop()"></div>
        }
      </div>
    </div>

    <!-- Same agenda as the classic Tages-/Wochenansicht. -->
    <div class="grid-legend">
      <div class="legend-item">
        <span class="legend-swatch legend-free"></span>
        <span>Frei</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch legend-individual"></span>
        <span>Einzelbuchung</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch legend-subscription"></span>
        <span>Abo Buchung</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch legend-course"></span>
        <span>Kurs</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch legend-event"></span>
        <span>Veranstaltung</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch legend-blocked"></span>
        <span>Gesperrt</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch legend-own"></span>
        <span>Eigene Buchung / Teilnahme</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch legend-selected"></span>
        <span>Aktuelle Auswahl</span>
      </div>
    </div>
  }
</div>
